import { useEffect, useState } from 'react'
import {
  faThumbsUp as faThumbsUpSolid,
  faThumbsDown as faThumbsDownSolid,
} from '@fortawesome/pro-solid-svg-icons'
import {
  faThumbsUp,
  faThumbsDown,
  faArrowRight,
} from '@fortawesome/pro-regular-svg-icons'
import { Icon, Button, Text } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'

interface FeedbackButtonsProps {
  initialRating?: -1 | 1
  onSubmit: (rating: -1 | 1, comment?: string) => void
  disabled?: boolean
}

export const FeedbackButtons = ({
  initialRating,
  onSubmit,
  disabled,
}: FeedbackButtonsProps) => {
  const [selected, setSelected] = useState<-1 | 1 | null>(initialRating ?? null)
  const [showComment, setShowComment] = useState(false)
  const [comment, setComment] = useState('')
  // True only after the user submits feedback in the current session —
  // used to show "Thanks for your feedback!" and hide the comment box.
  // Does NOT disable the vote buttons; users can always change their vote.
  const [submitted, setSubmitted] = useState(false)

  // Keep the visual state in sync with the parent's feedback map. Necessary
  // when votes arrive asynchronously (history hydration, post-stream refetch
  // flush, or submission-failure rollback) after the component has mounted.
  // Does not set submitted — that state only tracks current-session actions.
  useEffect(() => {
    setSelected(initialRating ?? null)
  }, [initialRating])

  const handleThumbsUp = () => {
    if (selected === 1) return // already selected
    setSelected(1)
    setShowComment(false)
    setSubmitted(true)
    onSubmit(1)
  }

  const handleThumbsDown = () => {
    // Clicking thumbs down while already selected re-opens the comment box
    if (selected === -1 && !showComment) {
      setShowComment(true)
      return
    }
    setSelected(-1)
    setSubmitted(false)
    setShowComment(true)
  }

  const handleCommentSubmit = () => {
    if (comment) {
      onSubmit(-1, comment)
    } else {
      onSubmit(-1)
    }
    setShowComment(false)
    setSubmitted(true)
  }

  return (
    <div className="mt-3 pt-3 border-t border-neutral-100 flex flex-col gap-2">
      <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
        Was this helpful?
      </Text>
      <div className="flex items-center gap-1">
        <button
          onClick={handleThumbsUp}
          disabled={disabled}
          aria-label="Rate response positively"
          className={mtc(
            'flex items-center justify-center size-6 rounded-[8px] bg-transparent border-none transition-colors',
            disabled ? 'cursor-default opacity-40' : 'cursor-pointer',
            selected === 1 ? 'text-green-100' : 'text-neutral-400',
            !disabled && 'hover:bg-[#f0eeea] hover:text-neutral-600'
          )}
        >
          <Icon
            icon={selected === 1 ? faThumbsUpSolid : faThumbsUp}
            className="text-xs"
          />
        </button>
        <button
          onClick={handleThumbsDown}
          disabled={disabled}
          aria-label="Rate response negatively"
          className={mtc(
            'flex items-center justify-center size-6 rounded-[8px] bg-transparent border-none transition-colors',
            disabled ? 'cursor-default opacity-40' : 'cursor-pointer',
            selected === -1 ? 'text-red-100' : 'text-neutral-400',
            !disabled && 'hover:bg-[#f0eeea] hover:text-neutral-600'
          )}
        >
          <Icon
            icon={selected === -1 ? faThumbsDownSolid : faThumbsDown}
            className="text-xs"
          />
        </button>
      </div>
      {submitted && (
        <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
          Thanks for your feedback!
        </Text>
      )}
      {!submitted && showComment && (
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-neutral-500">
            What went wrong? <span className="font-normal">(Optional)</span>
          </label>
          <div className="flex gap-2 items-center">
            {/* Custom textarea: the shared Textarea component has min-h-[80px] which
                breaks the single-line auto-grow UX needed here. */}
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onInput={(e) => {
                const el = e.currentTarget
                el.style.height = 'auto'
                el.style.height = `${el.scrollHeight}px`
              }}
              placeholder="Let us know how we can improve"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleCommentSubmit()
                }
              }}
              rows={1}
              className="flex-1 resize-none overflow-hidden text-sm rounded-[8px] border border-neutral-200 bg-neutral-0 px-3 py-[13px] text-neutral-700 placeholder:text-neutral-400 focus:outline-none focus:ring-1 focus:ring-neutral-300 leading-none min-h-10"
            />
            <Button
              variant="secondary"
              size="md"
              iconLeft={faArrowRight}
              onClick={handleCommentSubmit}
              aria-label="Submit feedback"
              className="aspect-square px-0 shrink-0"
            />
          </div>
        </div>
      )}
    </div>
  )
}
